import React, { ReactElement, useMemo } from 'react';

import { AsyncChildrenRowData, useAsyncChildrenRowData } from '../queries/reports';
import Spinner from '../../shared/spinner.component';

type ParsedAsyncRowData = {
  columns: Record<number, { value: unknown }>,
  children?: ParsedAsyncRowData[],
  description: string | null,
}

const parseAsyncRowData = (
  columnHeaders: string[],
  columnNumbers: number[],
  rows: AsyncChildrenRowData[]
): ParsedAsyncRowData[] =>
  rows.map(({ values, children, ...rest }) => ({
    columns: columnHeaders.reduce((acc, header, index) => {
      acc[columnNumbers[index]] = { value: values[header] };
      return acc;
    }, {} as Record<number, { value: unknown }>),
    ...children
      ? {
        children: parseAsyncRowData(columnHeaders, columnNumbers, children),
      }
      : null,
    ...rest,
  }));

type AsyncRowDataProviderProps = {
  url: string,
  columnHeaders: string[],
  columnNumbers: number[],
  renderRows: (rows: ParsedAsyncRowData[]) => ReactElement,
}

export const AsyncRowDataProvider: React.FC<AsyncRowDataProviderProps> = ({
  url,
  columnHeaders,
  columnNumbers,
  renderRows,
}: AsyncRowDataProviderProps) => {
  const { data: detailRows, isLoading } = useAsyncChildrenRowData({ detailsUrl: url });

  const childrenRows = useMemo(
    () => parseAsyncRowData(columnHeaders, columnNumbers, detailRows),
    [detailRows, columnHeaders, columnNumbers]
  );

  if (isLoading) {
    return (
      <div className="column col-4 child-description">
        <div className="d-flex align-items-center">
          <Spinner className="spinner-no-margin d-flex mr-2" small/>
          Loading...
        </div>
      </div>
    );
  }
  if (!childrenRows || !childrenRows.length) {
    return (
      <div className="column col-4 child-description">
        <div className="d-flex align-items-center">
          No rows to show
        </div>
      </div>
    );
  }

  return renderRows(childrenRows);
};
